import type { PageType } from './types';

export interface EvergreenSlugEntry {
  pageType: PageType;
  slug: string;
  lang: string;
  entityId: string;
  pageTitle: string;
  pageDescription: string;
  h2Title: string;
  data: Record<string, unknown>;
}

let _evergreenSlugCache: Record<string, EvergreenSlugEntry> | null = null;
let _evergreenReverseCache: Record<string, string> | null = null;
const _evergreenProviders: Array<() => EvergreenSlugEntry[]> = [];
const _readinessChecks: Array<() => boolean> = [];

/** Register a provider function that returns evergreen slug entries.
 *  Called by page type modules at import time. Providers are invoked lazily
 *  when `getAllSlugs()` or `getEvergreenSlugEntry()` is first called. */
export function registerEvergreenProvider(provider: () => EvergreenSlugEntry[]) {
  _evergreenProviders.push(provider);
  _evergreenSlugCache = null;
  _evergreenReverseCache = null;
}

/** Register a predicate that must return true before the registry is cached.
 *  Data-dependent providers (e.g. country-month, which gates slug generation
 *  on event counts) use this to signal their data source is loaded. Without
 *  it, a provider that silently returns [] before its data loads would be
 *  frozen out of an otherwise non-empty cache — see ensureEvergreenBuilt. */
export function registerEvergreenReadiness(check: () => boolean) {
  _readinessChecks.push(check);
}

export function ensureEvergreenBuilt() {
  if (_evergreenSlugCache) return;
  const cache: Record<string, EvergreenSlugEntry> = {};
  const reverse: Record<string, string> = {};
  for (const provider of _evergreenProviders) {
    const entries = provider();
    for (const entry of entries) {
      if (cache[entry.slug]) {
        const existing = cache[entry.slug];
        throw new Error(
          `Evergreen slug collision: "${entry.slug}" registered by both ` +
          `"${existing.pageType}" (${existing.lang}) and ` +
          `"${entry.pageType}" (${entry.lang}). ` +
          `Each slug must be unique across all evergreen providers.`
        );
      }
      cache[entry.slug] = entry;
      if (entry.entityId) {
        reverse[`${entry.entityId}:${entry.lang}`] = entry.slug;
      }
    }
  }
  // Don't cache an empty result — see slug-country.ts for the same
  // reasoning. A later call after preloads complete must be able to
  // rebuild with real data.
  if (Object.keys(cache).length === 0) return;
  // Don't cache a partially-built registry: if a data-dependent provider isn't
  // ready (e.g. events not yet loaded), it returns [] while other providers
  // populate the cache, which would otherwise freeze those entries out
  // permanently. Rebuild on a later call once data is present.
  if (_readinessChecks.some((check) => !check())) return;
  _evergreenSlugCache = cache;
  _evergreenReverseCache = reverse;
}

/** Resolve an evergreen slug to its entry, or null if not found */
export function getEvergreenSlugEntry(slug: string): EvergreenSlugEntry | null {
  ensureEvergreenBuilt();
  return _evergreenSlugCache?.[slug] ?? null;
}

/** Returns all registered evergreen slugs */
export function getAllEvergreenSlugs(): string[] {
  ensureEvergreenBuilt();
  return Object.keys(_evergreenSlugCache ?? {});
}

/** Returns alternate language versions of an evergreen page for hreflang tags. */
export function getAlternateEvergreenLanguages(entityId: string): Record<string, string> {
  ensureEvergreenBuilt();
  const result: Record<string, string> = {};
  for (const [key, slug] of Object.entries(_evergreenReverseCache ?? {})) {
    if (key.startsWith(`${entityId}:`)) {
      const lang = key.slice(entityId.length + 1);
      result[lang] = slug;
    }
  }
  return result;
}

/** Internal: access the raw evergreen slug cache for cross-registry functions */
export function _getEvergreenSlugCache(): Record<string, EvergreenSlugEntry> {
  ensureEvergreenBuilt();
  return _evergreenSlugCache ?? {};
}
